package dev.kuml.portal.web.i18n.legal

/**
 * The English legal-page content -- see this wave's plan §6.3 for the differentiated translation
 * strategy per page:
 *  - [imprint]: a courtesy translation is acceptable (mostly factual content); German legal norms
 *    (§ 5 TMG, § 18 (2) MStV) are cited, never "translated" into a non-existent English
 *    equivalent.
 *  - [privacy]: translated with GDPR terminology taken STRICTLY from the GDPR's own official
 *    English text (controller, processor, legitimate interests, right to object, ...) -- the
 *    English version is itself the information actually given to an English-reading data subject
 *    under Art. 12/13 GDPR, not merely a courtesy. Structurally parity-checked against
 *    [LegalPagesDe.privacy] (same 12 numbered sections, same order) -- see `LegalContentTest`.
 *  - [terms]: an EXPLICITLY NON-BINDING courtesy translation only (§ 305c (2) BGB risk, see the
 *    plan) -- a professional legal translation is recommended as a follow-up, NOT delivered by
 *    this wave (this wave's plan, decision E-4).
 *
 * Section/heading COUNT AND ORDER are kept identical to [LegalPagesDe] throughout -- a missing or
 * reordered section is exactly the kind of drift `LegalContentTest` exists to catch.
 */
public object LegalPagesEn : LegalPages {
    private const val IMPRINT_NOTICE =
        "This is a courtesy translation. Only the German version of this Impressum is legally " +
            "authoritative."
    private const val PRIVACY_NOTICE =
        "This English version is the information provided to English-reading data subjects under " +
            "Art. 12/13 GDPR. In case of any discrepancy with the German original, the German " +
            "version prevails for all matters of German law."
    private const val TERMS_NOTICE =
        "Courtesy translation. Only the German version of these Terms is legally binding; in case " +
            "of any discrepancy, the German version prevails. A professional legal translation of " +
            "these Terms has not yet been produced."

    override val imprint: LegalPage =
        legalPage("Imprint", notice = IMPRINT_NOTICE) {
            h("Information pursuant to § 5 TMG (German Telemedia Act)")
            address()

            h("Contact")
            p("Phone: +49 179 90 67 200")
            rich(txt("Email: "), lnk("irakli@betchvaia.de", "mailto:irakli@betchvaia.de"))

            h("Responsible for content pursuant to § 18 (2) MStV (German Interstate Media Treaty)")
            address(includeCountry = false)

            h("EU online dispute resolution")
            rich(
                txt("The European Commission provides a platform for online dispute resolution (ODR): "),
                lnk("https://ec.europa.eu/consumers/odr/", "https://ec.europa.eu/consumers/odr/", newTab = true),
            )
            p("You can find our email address above in this Imprint.")

            h("Consumer dispute resolution")
            p("We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.")

            h("Liability for content")
            p(
                "As a service provider, we are responsible for our own content on these pages under general law " +
                    "pursuant to § 7 (1) TMG. However, pursuant to §§ 8 to 10 TMG, we as a service provider are " +
                    "not obliged to monitor transmitted or stored third-party information or to investigate " +
                    "circumstances indicating unlawful activity. Obligations to remove or block the use of " +
                    "information under general law remain unaffected. Liability in this regard is only possible " +
                    "from the point in time at which a concrete infringement becomes known. Upon becoming aware " +
                    "of any such infringement, we will remove the relevant content immediately.",
            )

            h("Liability for links")
            p(
                "Our offering contains links to external third-party websites over whose content we have no " +
                    "influence. We therefore cannot accept any liability for this external content. The " +
                    "respective provider or operator of the linked pages is always responsible for their content.",
            )

            h("Copyright")
            rich(
                txt("The kUML project's source code is licensed under the "),
                lnk("Apache License 2.0", "https://www.apache.org/licenses/LICENSE-2.0", newTab = true),
                txt(". Content and works on these pages that are not expressly marked otherwise are subject to German copyright law."),
            )
        }

    override val privacy: LegalPage =
        legalPage("Privacy Policy", notice = PRIVACY_NOTICE) {
            p("Information on the processing of personal data when using the kUML Portal (app.kuml.dev) pursuant to Art. 13 GDPR.")

            h("1. Controller")
            address()
            rich(lnk("irakli@betchvaia.de", "mailto:irakli@betchvaia.de"))
            p("Phone: +49 179 90 67 200")

            h("2. Registration and user account")
            p(
                "During registration we collect your email address, a password of your choice (stored " +
                    "exclusively as an Argon2id hash, never in plain text), and a display name. The legal basis " +
                    "is Art. 6 (1)(b) GDPR (performance of the usage contract). Data is stored for as long as " +
                    "your account exists. You may request deletion of your account at any time, informally, by " +
                    "email to the address above; a self-service account-deletion feature does not currently exist.",
            )

            h("3. Sign-in (session cookie)")
            p(
                "After login we set a technically necessary cookie (KUML_PORTAL_SESSION) that maintains your " +
                    "sign-in across multiple page requests. It is httpOnly (not readable via JavaScript), " +
                    "transmitted only over an encrypted connection in production (Secure), and restricted to " +
                    "SameSite=Lax. Because this cookie is technically strictly necessary, no separate consent is " +
                    "required pursuant to § 25 (2) no. 2 TTDSG (German Telecommunications-Telemedia Data " +
                    "Protection Act). The legal basis is Art. 6 (1)(b) GDPR. The cookie becomes invalid upon " +
                    "logout or expiry.",
            )

            h("4. Project, team, and diagram data")
            p(
                "Projects you create (including diagram source code), project names and descriptions, and team " +
                    "memberships and invitations (email address of the invited person) are stored for as long " +
                    "as the respective project or team exists. The legal basis is Art. 6 (1)(b) GDPR.",
            )

            h("5. AI assistant")
            p(
                "If you use AI-assisted diagram generation, your prompt text (and, where applicable, your " +
                    "project's existing diagram source code) is transmitted to an external language-model " +
                    "provider. The operator determines server-side which provider is used — candidates are " +
                    "Anthropic PBC, OpenAI L.L.C., and Google LLC (all three based in the USA), or a self-hosted, " +
                    "local model (Ollama) that sends no data off our own server. When a US provider is used, " +
                    "this constitutes a transfer to a third country; it takes place on the basis of the European " +
                    "Commission's Standard Contractual Clauses and/or any applicable adequacy decision for the " +
                    "respective provider. The legal basis for the transfer is Art. 6 (1)(b) GDPR, since AI " +
                    "generation is a contractual service you have actively requested. The currently used " +
                    "provider can be requested from the controller.",
            )

            h("6. AI training and renderer improvement")
            p(
                "Every AI generation you trigger in the Portal is additionally stored, alongside your project's " +
                    "current state, as its own history entry: timestamp, your account, the project, your prompt " +
                    "text, the generated kUML source code, the graphic rendered from it, and the renderer " +
                    "version in use at that time. Intermediate states from real-time collaboration are not " +
                    "stored.",
            )
            p("This data serves two separate purposes, each with its own legal basis:")
            bullets(
                "Renderer bug fixing: if you report a diagram as incorrect, we can reproduce and fix the bug " +
                    "from the source code, graphic, and renderer version. A comment thread allows follow-up " +
                    "questions between you and us. The legal basis is Art. 6 (1)(f) GDPR (legitimate interest " +
                    "in the correctness and further development of the service). This purpose applies to all " +
                    "plans.",
                "Training our own AI model: generations from the free Free plan may be used to train a language " +
                    "model we operate ourselves, exclusively on our own infrastructure. No transfer to an " +
                    "external provider takes place for this purpose (distinct from Section 5, which concerns " +
                    "generation itself). The legal basis is likewise Art. 6 (1)(f) GDPR; the free offering is " +
                    "partly financed through this use. Generations from paid plans (Pro/Team) are not used for " +
                    "this purpose.",
                "Enhanced use (showcase): only if you explicitly enable this in your profile, your diagrams may " +
                    "be prioritised in the training dataset and used as a public example (for instance on our " +
                    "website or in presentation material). The legal basis is exclusively your consent under " +
                    "Art. 6 (1)(a) GDPR. This consent is not granted by default.",
            )
            p(
                "Retention period: non-reported generations are automatically limited per project to the 50 " +
                    "most recent entries; older entries are deleted. Reported generations are retained until the " +
                    "bug-fixing process is complete. All entries are deleted at the latest upon deletion of the " +
                    "associated project. Deleting your account removes your author attribution from remaining " +
                    "entries (including entries belonging to a shared team project, which therefore remain with " +
                    "your team after you leave it) as well as your own contributions to the comment thread; the " +
                    "remaining diagram content itself is not thereby deleted as long as the associated project " +
                    "continues to exist. If you additionally want the content of your own generations deleted, " +
                    "please state this explicitly with your account-deletion request (see Section 2).",
            )
            p(
                "Right to object (Art. 21 GDPR): you may object at any time to the use of your diagrams for " +
                    "training and bug-fixing purposes. It is sufficient to disable the switch \"My diagrams may " +
                    "be used to fix renderer bugs and to train kUML's own, locally operated AI model\" in your " +
                    "profile; no reason is required, and an email to the controller is equally sufficient. The " +
                    "objection takes immediate effect for all future generations and additionally for already-" +
                    "stored generations that have not yet been used: our selection process checks the current " +
                    "state of your account on every use. Training runs that have already completed cannot be " +
                    "undone retroactively.",
            )
            p(
                "Withdrawal of consent: you may withdraw the separate showcase consent at any time with effect " +
                    "for the future by disabling the corresponding switch in your profile. The lawfulness of " +
                    "processing carried out up to the withdrawal remains unaffected (Art. 7 (3) GDPR). We remove " +
                    "already-published examples upon your request.",
            )
            p(
                "Both switches are independent of one another and have no effect on your feature access — in " +
                    "particular, an objection neither restricts nor makes the Free plan more expensive.",
            )

            h("7. Payment processing (paid plans)")
            p(
                "For paid plans (Pro/Team) we work with Lemon Squeezy (Lemon Squeezy, Inc., USA) as merchant of " +
                    "record. When purchasing or managing a subscription you are redirected to a checkout or " +
                    "customer-portal page hosted by Lemon Squeezy itself — there, Lemon Squeezy processes your " +
                    "payment data as an independent controller under data protection law; Lemon Squeezy's own " +
                    "privacy policy applies. We ourselves store only your plan/subscription status and the " +
                    "customer/subscription ID assigned by Lemon Squeezy — never credit card or other payment " +
                    "data. The legal basis is Art. 6 (1)(b) GDPR.",
            )

            h("8. Hosting / server log files")
            p("The kUML Portal is hosted at:")
            lines("netcup GmbH", "Daimlerstraße 25", "76185 Karlsruhe", "Germany")
            p(
                "When accessing app.kuml.dev, the web server automatically records technical data in server log " +
                    "files (IP address, date/time, requested address, HTTP status code, referrer, user agent). " +
                    "The legal basis is Art. 6 (1)(f) GDPR (legitimate interest in secure and stable operation). " +
                    "A data processing agreement pursuant to Art. 28 GDPR is in place with our host, netcup.",
            )

            h("9. No tracking, no advertising")
            p(
                "Other than the session cookie described in Section 3, the Portal sets no further cookies, uses " +
                    "no web analytics (no Google Analytics, no Matomo), and no advertising scripts.",
            )
            p(
                "For the pure language selection (German/English), the Portal stores only the value de or en " +
                    "under the key kportal.lang in your device's browser localStorage. This is technically " +
                    "strictly necessary for the feature you yourself requested (retaining your chosen language " +
                    "across multiple page requests) and therefore requires no separate consent pursuant to " +
                    "§ 25 (2) no. 2 TTDSG (German Telecommunications-Telemedia Data Protection Act). This " +
                    "information has no personal reference and is never transmitted to our server.",
            )

            h("10. Your rights")
            p("You have the following rights vis-à-vis the controller regarding personal data concerning you:")
            bullets(
                "Access (Art. 15 GDPR)",
                "Rectification (Art. 16 GDPR)",
                "Erasure (Art. 17 GDPR)",
                "Restriction of processing (Art. 18 GDPR)",
                "Data portability (Art. 20 GDPR)",
                "Objection to processing (Art. 21 GDPR)",
            )
            rich(
                txt("An informal email to "),
                lnk("irakli@betchvaia.de", "mailto:irakli@betchvaia.de"),
                txt(" is sufficient to exercise these rights."),
            )

            h("11. Right to complain to a supervisory authority")
            p(
                "You have the right to lodge a complaint with a data protection supervisory authority about the " +
                    "processing of your personal data (Art. 77 GDPR). The competent authority is:",
            )
            lines("Die Landesbeauftragte für den Datenschutz Niedersachsen", "Prinzenstraße 5", "30159 Hannover, Germany")
            rich(lnk("lfd.niedersachsen.de", "https://lfd.niedersachsen.de", newTab = true))

            h("12. Currency and changes to this privacy policy")
            p("This privacy policy is current as of August 2026. Further development of the Portal may require updates.")

            h("13. Share links")
            p(
                "You can share a single diagram with third parties via a share link. A visitor who opens such a " +
                    "link sees only the diagram itself — neither the project name, nor the team name, nor your " +
                    "account details are shown to them or exposed via the underlying API routes. If the link is " +
                    "password-protected, the entered password is verified server-side (Argon2id hash) and never " +
                    "stored in plain text.",
            )
            p(
                "For each share link, we store: the SHA-256 hash of the link token (never the token itself), " +
                    "optionally the Argon2id hash of a password, the creation time, an optional expiry date, and " +
                    "the time it was last opened. A visitor's IP address is NOT stored. The legal basis is " +
                    "Art. 6(1)(b) GDPR (fulfilling the sharing feature you actively requested). You can revoke a " +
                    "share link at any time; it is then irrevocably deleted along with its hash.",
            )

            h("14. Social login (Google / GitHub)")
            p(
                "Instead of an email address and password, you can also register or sign in using a Google " +
                    "or GitHub account. Doing so redirects you to the sign-in page of the chosen provider, " +
                    "where you authenticate with your own credentials there. After you consent, the provider " +
                    "sends us: a stable, provider-side account identifier (Google's so-called \"sub\" claim, " +
                    "or your numeric GitHub user ID), the primary email address on file with that provider " +
                    "along with its verification status there, and the display name on file there. A profile " +
                    "picture is not adopted. The legal basis is Art. 6(1)(b) GDPR, since you actively choose " +
                    "this sign-in method yourself.",
            )
            p(
                "The providers are Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland " +
                    "(part of Google LLC, USA) and GitHub, Inc., 88 Colin P. Kelly Jr. Street, San Francisco, " +
                    "USA (part of Microsoft Corporation). Insofar as data is transferred to the USA in this " +
                    "process, this constitutes a transfer to a third country; it takes place on the basis of " +
                    "the European Commission's standard contractual clauses or any applicable adequacy " +
                    "decision for the respective provider (see section 5).",
            )
            p(
                "We permanently store only: the provider, the stable account identifier, the email address " +
                    "reported at the time of linking together with its verification status at that time " +
                    "(for evidentiary/support purposes only, not a value kept continuously in sync), the time " +
                    "the account was linked, and the time of the last sign-in via this method. We never store " +
                    "the provider's access or refresh tokens; they are used exactly once during sign-in and " +
                    "then discarded.",
            )
            p(
                "You can remove an existing link at any time in your profile, provided your account still " +
                    "has at least one other sign-in method (a password you have set, or a second linked " +
                    "provider) — otherwise, first set a password via \"Forgot password\".",
            )
        }

    override val terms: LegalPage =
        legalPage("Terms and Conditions", notice = TERMS_NOTICE) {
            p("As of: August 2026")

            h("1. Scope, contracting party")
            p(
                "These Terms govern use of the kUML Portal (app.kuml.dev), a paid online service for modelling, " +
                    "collaboratively editing, and AI-assisted generation of UML/kUML diagrams. The contracting " +
                    "party is:",
            )
            address()
            rich(lnk("irakli@betchvaia.de", "mailto:irakli@betchvaia.de"))
            p(
                "These Terms apply both to consumers (§ 13 BGB) and to businesses (§ 14 BGB). Deviating, " +
                    "conflicting, or supplementary general terms of the user do not become part of the contract " +
                    "unless their applicability is expressly agreed to in writing.",
            )

            h("2. Description of services")
            p(
                "Depending on the chosen plan (Free/Pro/Team), the kUML Portal offers, among other things: " +
                    "creating and managing diagram projects, AI-assisted diagram generation from free-text " +
                    "prompts, real-time collaboration by multiple users on one diagram, and team management. The " +
                    "specific feature and quota scope of each plan (project caps, AI generations per month, team " +
                    "size) as well as the currently applicable prices are shown during checkout and in the user " +
                    "account, and are not part of these Terms, in order to avoid a discrepancy between the terms " +
                    "set out here and the conditions actually offered.",
            )

            h("3. Formation of contract")
            p(
                "Registering a free Free account forms a usage contract on the terms described in Section 2. For " +
                    "paid plans (Pro/Team), the contract is only formed upon successful completion of the order " +
                    "process via Lemon Squeezy's payment processing (see Section 4).",
            )

            h("4. Prices, payment, payment processing via Lemon Squeezy")
            p(
                "For paid plans we work with Lemon Squeezy (Lemon Squeezy, Inc., USA) as merchant of record. The " +
                    "usage contract for the Portal service itself is formed with the contracting party named " +
                    "above — Lemon Squeezy handles only payment processing, acting legally as the seller of " +
                    "record for the payment and is, to that extent, responsible for invoicing, VAT remittance, " +
                    "and payment data; Lemon Squeezy's own terms and conditions apply additionally to the " +
                    "payment process. All prices include applicable statutory VAT, where applicable.",
            )

            h("5. Term and cancellation of paid plans")
            p(
                "Paid subscriptions run until cancelled and may be cancelled monthly, effective at the end of " +
                    "the respective billing period. Cancellation is done via the Lemon Squeezy customer portal, " +
                    "accessible from your own user account. After cancellation, access remains available until " +
                    "the end of the already-paid billing period; the account is then automatically downgraded to " +
                    "the Free plan, not deleted.",
            )

            h("6. Right of withdrawal for consumers")
            p(
                "Consumers generally have a statutory right of withdrawal for distance contracts. This right of " +
                    "withdrawal expires prematurely for contracts concerning digital content/services if we have " +
                    "begun performance of the contract after the consumer has expressly agreed that we begin " +
                    "performance before expiry of the withdrawal period, and the consumer has confirmed their " +
                    "awareness that, by giving this consent, they lose their right of withdrawal upon " +
                    "commencement of performance (§ 356 (5) BGB). This consent is obtained separately when " +
                    "booking a paid plan.",
            )

            h("7. Rights of use in own content")
            p(
                "All rights in diagrams, prompts, and other content created by the user remain with the user. " +
                    "The user grants us only the simple rights of use necessary to provide the Portal service — " +
                    "in particular for storage, display, and processing by the AI provider chosen by the " +
                    "operator (see Privacy Policy, Section \"AI assistant\"), as well as for collaborative " +
                    "editing by team members the user has invited, and, to the extent described in the Privacy " +
                    "Policy, Section \"AI training and renderer improvement\", for bug fixing and model " +
                    "improvement. These rights of use end upon deletion of the respective content or termination " +
                    "of the contract.",
            )

            h("8. User obligations")
            p(
                "The user undertakes to provide truthful information upon registration, to keep their access " +
                    "credentials confidential, and to inform us without delay of any misuse of their account. " +
                    "The user may not create or distribute unlawful content via the Portal.",
            )
            p(
                "In particular regarding third parties' personal data: if a diagram created by the user contains " +
                    "personal data of third parties (e.g. an organisational chart with real names), the user is " +
                    "solely responsible for having their own legal basis under data protection law for doing so. " +
                    "The user indemnifies us against third-party claims arising from a breach of this obligation " +
                    "by the user.",
            )

            h("9. Availability")
            p(
                "We strive for the Portal to be available as uninterruptedly as possible, but do not guarantee " +
                    "any particular availability. Maintenance work that may lead to temporary restrictions will " +
                    "be announced where possible.",
            )

            h("10. Liability")
            p(
                "We are liable without limitation for intent and gross negligence, as well as pursuant to the " +
                    "Product Liability Act, for injury to life, body, or health, and to the extent of any " +
                    "guarantee we have assumed. For slightly negligent breach of material contractual " +
                    "obligations (cardinal obligations), whose fulfilment is essential to the proper performance " +
                    "of the contract and on whose observance the user may regularly rely, liability is limited " +
                    "to the foreseeable damage typical for the type of contract at the time of contract " +
                    "formation. Beyond that, liability for slightly negligent breaches of duty is excluded.",
            )

            h("11. Data protection")
            rich(
                txt("Information on the processing of personal data can be found in our "),
                lnk("Privacy Policy", "/datenschutz"),
                txt("."),
            )

            h("12. Changes to these Terms")
            p(
                "We may amend these Terms with effect for the future, to the extent this is necessary to adapt " +
                    "to changed legal or technical circumstances, or does not disadvantage the user's existing " +
                    "contractual position. We inform users of changes by email to the address stored in the " +
                    "account, with at least four weeks' notice before the change takes effect. If the user does " +
                    "not object within this period, the amended Terms are deemed accepted; we point out this " +
                    "consequence separately in the change notice. In the event of an objection, the right to " +
                    "ordinary termination remains unaffected.",
            )

            h("13. Final provisions")
            p(
                "German law applies, excluding the UN Convention on Contracts for the International Sale of " +
                    "Goods. If the user is a merchant within the meaning of the German Commercial Code, a legal " +
                    "entity under public law, or a special fund under public law, Braunschweig is the place of " +
                    "jurisdiction for all disputes arising from this contractual relationship. Vis-à-vis " +
                    "consumers this applies only within the scope of statutory provisions. Should individual " +
                    "provisions of these Terms be or become invalid, the validity of the remaining provisions " +
                    "remains unaffected.",
            )
            p(
                "These Terms are concluded in the German language. Any English-language version is provided " +
                    "for information only; in the event of any discrepancy, only the German version is " +
                    "authoritative.",
            )
        }
}

private fun MutableList<LegalBlock>.address(includeCountry: Boolean = true) {
    if (includeCountry) {
        lines("Irakli Betchvaia", "Wendenring 22", "38114 Braunschweig", "Germany")
    } else {
        lines("Irakli Betchvaia", "Wendenring 22", "38114 Braunschweig")
    }
}
